/**
 * Public sponsor invite acceptance page.
 *
 * Reached from the "Accept sponsorship invite" email, which carries only the
 * invite_token — no embed key, no login. The invited assignment (and its
 * reserved slot) already exists; accepting settles payment (Stripe for paid
 * tiers, a straight confirm for free/comped ones) and activates it. On
 * success we hand off to the emailed asset-upload link if fulfillment is
 * still incomplete.
 */
import { useEffect, useMemo, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';

import {
  getSponsorInvite,
  checkoutSponsorInvite,
  confirmSponsorInvite,
  type SponsorInviteData,
  type SponsorInviteCheckout,
  type SponsorInviteConfirmResult,
} from '@/api/embed';
import { PaymentForm } from '@/components/PaymentForm';
import { themedStripeAppearance } from '@/styles/stripeAppearance';
import './PaymentPage.css';
import './SponsorFulfillmentPage.css';

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '';

interface Props {
  token: string;
}

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: cents % 100 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

export function SponsorInvitePage({ token }: Props) {
  const [invite, setInvite] = useState<SponsorInviteData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [checkout, setCheckout] = useState<SponsorInviteCheckout | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [result, setResult] = useState<SponsorInviteConfirmResult | null>(null);

  const stripe = useMemo<Promise<Stripe | null> | null>(() => {
    if (!STRIPE_PUBLISHABLE_KEY) return null;
    return loadStripe(STRIPE_PUBLISHABLE_KEY);
  }, []);

  useEffect(() => {
    let alive = true;
    getSponsorInvite(token)
      .then((d) => alive && setInvite(d))
      .catch((err) => {
        if (!alive) return;
        setLoadError(
          err?.response?.status === 404
            ? "This invite link isn't valid anymore. If you think that's a mistake, reply to the email you received."
            : 'Could not load your invite. Please try again in a moment.',
        );
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const runConfirm = async (paymentIntentId?: string | null) => {
    const res = await confirmSponsorInvite(token, paymentIntentId);
    setResult(res);
  };

  const onAccept = async () => {
    setActionError(null);
    setBusy(true);
    try {
      const co = await checkoutSponsorInvite(token);
      if (co.is_free) {
        await runConfirm(null);
      } else {
        setCheckout(co);
      }
    } catch (err: any) {
      setActionError(
        err?.response?.status === 409
          ? 'This invite has already been accepted.'
          : err?.response?.data?.detail || 'Could not start acceptance. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="tn-fulfill">
        <h1 className="tn-fulfill__title">Sponsorship invite</h1>
        <div role="alert" className="tn-fulfill__error">{loadError}</div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="tn-fulfill">
        <p className="tn-fulfill__loading">Loading…</p>
      </div>
    );
  }

  // Post-acceptance success state.
  if (result) {
    return (
      <div className="tn-fulfill">
        <h1 className="tn-fulfill__title">You're a sponsor 🎉</h1>
        <p className="tn-fulfill__subtitle">
          Thanks, {invite.sponsor_name}! {result.message} Confirmation{' '}
          <strong>{result.confirmation_number}</strong>.
        </p>
        {result.fulfillment_url ? (
          <>
            <p className="tn-fulfill__subtitle">
              One more step — upload your logo and branding so we can display it
              at the event.
            </p>
            <a className="tn-btn tn-btn--primary" href={result.fulfillment_url}>
              Upload sponsor assets
            </a>
          </>
        ) : (
          <p className="tn-fulfill__subtitle">You're all set — you can close this window.</p>
        )}
      </div>
    );
  }

  if (invite.already_accepted) {
    return (
      <div className="tn-fulfill">
        <h1 className="tn-fulfill__title">Already accepted</h1>
        <p className="tn-fulfill__subtitle">
          Thanks, {invite.sponsor_name} — this sponsorship for {invite.tier_name}{' '}
          at {invite.event_name} is already confirmed. Check your email for the
          asset-upload link.
        </p>
      </div>
    );
  }

  if (invite.expired) {
    return (
      <div className="tn-fulfill">
        <h1 className="tn-fulfill__title">Invite expired</h1>
        <p className="tn-fulfill__subtitle">
          This sponsorship invite for {invite.event_name} has expired. Reply to
          the email you received and the organizer can send a fresh invite.
        </p>
      </div>
    );
  }

  // Paid invite, after Accept, but Stripe isn't configured (dev) — let the
  // rest of the flow be exercised. Mirrors SponsorPaymentPage's dev skip.
  if (checkout && !checkout.is_free && !STRIPE_PUBLISHABLE_KEY) {
    return (
      <div className="tn-card tn-payment__dev-skip">
        <h2>Payment (dev mode)</h2>
        <p>Stripe publishable key not set. Skipping payment for testing.</p>
        <button
          className="tn-btn tn-btn--primary"
          onClick={() => { void runConfirm(checkout.stripe_payment_intent_id); }}
        >
          Mark as paid (dev)
        </button>
      </div>
    );
  }

  // Stripe payment step (paid invite, after Accept).
  if (checkout && !checkout.is_free && checkout.stripe_client_secret && stripe) {
    return (
      <div className="tn-payment">
        <h1 className="tn-payment__title">Payment</h1>
        <p className="tn-payment__subtitle">{invite.tier_name}</p>
        <div className="tn-payment__breakdown">
          <div className="tn-payment__line">
            <span>Sponsorship</span>
            <span>{money(checkout.order_summary.price_cents)}</span>
          </div>
          {(checkout.order_summary.platform_fee_cents ?? 0) > 0 ? (
            <div className="tn-payment__line">
              <span>Platform fee</span>
              <span>{money(checkout.order_summary.platform_fee_cents ?? 0)}</span>
            </div>
          ) : null}
          {checkout.order_summary.processing_fee_cents > 0 ? (
            <div className="tn-payment__line">
              <span>Processing fee</span>
              <span>{money(checkout.order_summary.processing_fee_cents)}</span>
            </div>
          ) : null}
          <div className="tn-payment__line tn-payment__line--total">
            <span>Total</span>
            <span>{money(checkout.order_summary.total_cents)}</span>
          </div>
        </div>
        <Elements
          stripe={stripe}
          options={{
            clientSecret: checkout.stripe_client_secret,
            appearance: themedStripeAppearance(),
          }}
        >
          <PaymentForm
            submitLabel="Pay and accept"
            onSuccess={() => runConfirm(checkout.stripe_payment_intent_id)}
          />
        </Elements>
        {actionError ? <div role="alert" className="tn-payment__error">{actionError}</div> : null}
      </div>
    );
  }

  // Details + accept.
  const isFree = invite.total_cents <= 0;
  return (
    <div className="tn-fulfill">
      <h1 className="tn-fulfill__title">You're invited to sponsor</h1>
      <p className="tn-fulfill__subtitle">
        Hi {invite.sponsor_name} — you've been invited to sponsor{' '}
        {invite.event_name} as {invite.tier_name}. Review the details and accept
        below.
      </p>

      <div className="tn-payment__breakdown">
        <div className="tn-payment__line">
          <span>Sponsorship</span>
          <span>{money(invite.price_cents)}</span>
        </div>
        {(invite.platform_fee_cents ?? 0) > 0 ? (
          <div className="tn-payment__line">
            <span>Platform fee</span>
            <span>{money(invite.platform_fee_cents ?? 0)}</span>
          </div>
        ) : null}
        {invite.processing_fee_cents > 0 ? (
          <div className="tn-payment__line">
            <span>Processing fee</span>
            <span>{money(invite.processing_fee_cents)}</span>
          </div>
        ) : null}
        <div className="tn-payment__line tn-payment__line--total">
          <span>Total</span>
          <span>{isFree ? 'Free' : money(invite.total_cents)}</span>
        </div>
      </div>

      {actionError ? <div role="alert" className="tn-fulfill__error">{actionError}</div> : null}

      <button
        type="button"
        className="tn-btn tn-btn--primary tn-fulfill__editagain"
        onClick={onAccept}
        disabled={busy}
      >
        {busy ? 'Working…' : isFree ? 'Accept sponsorship' : 'Accept & pay'}
      </button>
    </div>
  );
}
